import { useEffect, useState } from 'react';
import { __t } from '../../../i18n';
import { IconVolumeOff } from '../../../icons';
import { ActionBarBtn, UnmuteNoticeWrapper, UnmuteScreenDetector } from '../../Player/styles';
import { AUTOPLAY_SUCCESS, autoplayStarted } from '../../Player/PlayerServices';

// The mp4 twin of the video.js player's notice. Playback starts muted only when the browser
// refused to autoplay with sound and the player retried muted (Player.tsx, or playLinearAd for
// a pre-roll), so the viewer never asked for it: ask them for the click browsers require before
// the sound can be turned on.
export default function UnmuteNotice({ video }: { video: HTMLVideoElement }) {
  const [showNotice, setShowNotice] = useState<boolean>(false);

  useEffect(() => {
    // AUTOPLAY_SUCCESS is how the player reports that its autoplay attempt started playback,
    // so an element muted at that point is one that was not allowed to start with sound. The
    // muted state is read from the element rather than carried on the event because an ad break
    // plays on an overlay of its own and the mute is mirrored back here - and the attempt can
    // settle before this mounts, hence the initial read for an element already playing.
    const handleAutoplayOutcome = () => setShowNotice(video.muted);
    const handleVolumeChange = () => {
      if (!video.muted) setShowNotice(false);
    };

    if (autoplayStarted(video) || !video.paused) handleAutoplayOutcome();
    // Once, like the video.js notice's player.one(): every later break announces an outcome of
    // its own, and a mute the viewer chose in between is not ours to offer undoing.
    video.addEventListener(AUTOPLAY_SUCCESS, handleAutoplayOutcome, { once: true });
    video.addEventListener('volumechange', handleVolumeChange);

    return () => {
      video.removeEventListener(AUTOPLAY_SUCCESS, handleAutoplayOutcome);
      video.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [video]);

  const handleUnmute = () => {
    video.muted = false;
    // Unmuting onto a zero level would be silent anyway. The Vast plugin mirrors both onto the
    // ad overlay, so this is what turns the sound on mid-break too.
    if (!video.volume) video.volume = 1;
  };

  if (!showNotice) return null;

  return (
    <>
      <UnmuteScreenDetector onClick={handleUnmute} />
      <UnmuteNoticeWrapper>
        <ActionBarBtn onClick={handleUnmute}>
          {__t('player.unmute_notice')}
          <IconVolumeOff />
        </ActionBarBtn>
      </UnmuteNoticeWrapper>
    </>
  );
}
